import { Button, Dialog, IconButton } from '@mui/material';
import { FilterKeys } from '@schooly/api';
import {
  AgeGroupSelectMultiple,
  FilterRow,
  SubjectSelectMultiple,
  toggleMultipleValueArrayProperty,
} from '@schooly/components/filters';
import { SchoolUserRole } from '@schooly/constants';
import { DROPDOWN } from '@schooly/constants/test-ids';
import { CrossIcon, ModalContent, ModalFooter, ModalHeader } from '@schooly/style';
import { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useSchool } from '../../../../hooks/useSchool';

export type TimetableGroupFilters = Partial<{
  [FilterKeys.AgeGroup]: string[];
  [FilterKeys.Subject]: string[];
  [FilterKeys.OnlyTutorGroups]: string[];
}>;

type TimetableGroupFilterPopupProps = {
  filters: TimetableGroupFilters;
  availableAgeGroupIds: string[];
  showSubjectFilter?: boolean;
  onClose: () => void;
  onSetFilters: (v: TimetableGroupFilters) => void;
};

export const TimetableGroupFilterPopup: FC<TimetableGroupFilterPopupProps> = ({
  onClose,
  onSetFilters,
  filters: actualFilters,
  availableAgeGroupIds,
  showSubjectFilter = true,
}) => {
  const { formatMessage } = useIntl();
  const { schoolId } = useSchool();
  const [filters, setFilters] = useState<TimetableGroupFilters>(actualFilters);

  const isAgeGroupRequired = availableAgeGroupIds.length > 0;

  const handleApply = useCallback(() => {
    onSetFilters(filters);
    onClose();
  }, [onSetFilters, filters, onClose]);

  const handleClear = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      [FilterKeys.AgeGroup]: availableAgeGroupIds,
      [FilterKeys.Subject]: [],
    }));
  }, [availableAgeGroupIds]);

  const { onClearSubject, onSelectSubjectId, onSelectAgeGroup, onClearAgeGroup } = useMemo(() => {
    const updateFilter = (key: keyof TimetableGroupFilters) => (id: string) => {
      setFilters((filters) => ({
        ...filters,
        [key]: filters[key]?.includes(id)
          ? filters[key]?.filter((ct) => ct !== id)
          : [...(filters[key] || []), id],
      }));
    };

    const clearFilter = (key: keyof TimetableGroupFilters) => () => {
      setFilters((filters) => ({
        ...filters,
        [key]: [],
      }));
    };

    return {
      onSelectSubjectId: updateFilter(FilterKeys.Subject),
      onClearSubject: clearFilter(FilterKeys.Subject),
      onSelectAgeGroup: (v: string[]) => {
        setFilters((filters) => {
          const next = toggleMultipleValueArrayProperty(filters[FilterKeys.AgeGroup], v);
          return {
            ...filters,
            [FilterKeys.AgeGroup]: isAgeGroupRequired && !next.length ? v.slice(0, 1) : next,
          };
        });
      },
      onClearAgeGroup: clearFilter(FilterKeys.AgeGroup),
    };
  }, [isAgeGroupRequired]);

  if (!schoolId) return null;

  return (
    <Dialog
      fullWidth
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        zIndex: theme.zIndex.modal + 1,

        '& .MuiDialog-paperFullWidth': {
          width: 600,
          height: 'unset',
        },
      })}
      open
      onClose={onClose}
    >
      <ModalHeader active title={formatMessage({ id: 'groups-Filter-Title' })}>
        <IconButton onClick={onClose}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>
      <ModalContent active>
        <FilterRow label={formatMessage({ id: 'schoolProperty-AgeGroup' })}>
          <AgeGroupSelectMultiple
            testId={DROPDOWN.AGE_GROUP}
            borderOnHover
            allowArchived
            schoolId={schoolId}
            onClear={isAgeGroupRequired ? undefined : onClearAgeGroup}
            optionsTitle={formatMessage({ id: 'schoolProperty-AgeGroup-plural' })}
            userRole={SchoolUserRole.Student}
            availableIds={availableAgeGroupIds}
            selectedIds={filters.age_group || []}
            onSelect={onSelectAgeGroup}
            required={isAgeGroupRequired}
          />
        </FilterRow>
        {showSubjectFilter && (
          <FilterRow label={formatMessage({ id: 'groups-GroupSubject' })}>
            <SubjectSelectMultiple
              testId={DROPDOWN.SUBJECT}
              borderOnHover
              schoolId={schoolId}
              onClear={onClearSubject}
              selectedIds={filters.subject || []}
              allowArchived
              onSelectSubjectId={onSelectSubjectId}
            />
          </FilterRow>
        )}
      </ModalContent>
      <ModalFooter active>
        <Button variant="outlined" onClick={handleClear}>
          {formatMessage({ id: 'filter-Clear' })}
        </Button>
        <Button onClick={handleApply}>{formatMessage({ id: 'filter-Apply' })}</Button>
      </ModalFooter>
    </Dialog>
  );
};
